import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TablePagesTitle from "../../../components/table-pages-title/TablePagesTitle";
import AccessPageAction from "../page-action/AccessPageAction";
import CreateRoleSkeleton from "../../../components/skeletons/CreateRoleSkeleton";
import "./../accessmanagement.scss";
import {
  buttonName,
  commonValidationMsg,
  permissionManagement,
  roleMange,
  userManage
} from "../../../locales/en";
import { CREATE_USER_PERMISSION, USER_PERMISSION } from "../../../route/paths";
import { getActiveStatusOptions } from "../../../utils/getDropdownOptions";
import {
  formatEndpointValue,
  formatMethodValue,
  getSelectedNavItemNames,
  showToast
} from "../../../utils/utils";
import { showToastType } from "../../../utils/constants/appConstants";
import privateAxios from "../../../utils/axiosPrivate";
import {
  getUserManagementPermissionsDataURL,
  getUserManagementPermissionsURL,
  navItemsURL
} from "../../../utils/constants/apiEndPoints";
const initialPermissionData = {
  name: "",
  isActive: "false",
  endpoint: "",
  description: "",
  method: ""
};
const initialErrorData = {
  name: "",
  endpoint: "",
  permissions: "",
  method: ""
};
const PermissionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(true);
  const [checkedItems, setCheckedItems] = useState({});
  const [adminMenuPermissions, setAdminMenuPermissions] = useState([]);
  const [permissionData, setPermissionData] = useState(initialPermissionData);
  const [errorData, setErrorData] = useState(initialErrorData);
  const clearPermissionError = () => {
    setErrorData((prev) => ({
      ...prev,
      permissions: ""
    }));
  };
  const handleSelectAll = (itemId, checked) => {
    setCheckedItems((prev) => {
      const updated = { ...prev, [itemId]: checked };
      const item = adminMenuPermissions.find((i) => i.id === itemId);
      item?.children?.forEach((child) => {
        updated[child.id] = checked;
      });
      return updated;
    });
    if (checked) clearPermissionError();
  };
  const handleChildChange = (itemId, childId, checked) => {
    setCheckedItems((prev) => {
      const updated = { ...prev, [childId]: checked };
      const parent = adminMenuPermissions.find((i) => i.id === itemId);
      updated[itemId] = parent?.children?.every((child) => updated[child.id]);
      return updated;
    });
    if (checked) clearPermissionError();
  };
  const handleInputChange = ({ target: { name, value } }) => {
    let updatedValue = value;
    if (name === "method") {
      updatedValue = formatMethodValue(value);
    }
    if (name === "endpoint") {
      updatedValue = formatEndpointValue(value);
    }
    setPermissionData((prev) => ({
      ...prev,
      [name]: updatedValue
    }));
    setErrorData((prev) => ({
      ...prev,
      [name]: ""
    }));
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const navPromise = privateAxios.get(navItemsURL);
        const permissionPromise = isEdit
          ? privateAxios.get(getUserManagementPermissionsDataURL(id))
          : Promise.resolve(null);
        const [navRes, permissionRes] = await Promise.all([
          navPromise,
          permissionPromise
        ]);
        const navItems = navRes?.data?.data || [];
        setAdminMenuPermissions(navItems);
        if (isEdit) {
          const permission = permissionRes?.data?.data || {};
          setPermissionData(permission);
          const selectedItems = {};
          navItems.forEach((item) => {
            if (permission?.navItemSlugs?.includes(item.slugName)) {
              selectedItems[item.id] = true;
            }
            item.children?.forEach((child) => {
              if (permission?.navItemSlugs?.includes(child.slugName)) {
                selectedItems[child.id] = true;
              }
            });
          });
          setCheckedItems(selectedItems);
        }
      } catch (error) {
        showToast(
          error?.response?.data?.message || error.message,
          showToastType.error
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEdit]);
  const validateForm = () => {
    const errors = {};
    if (!permissionData.name.trim()) {
      errors.name = commonValidationMsg.permissionName;
    }
    if (!permissionData.endpoint.trim()) {
      errors.endpoint = commonValidationMsg.endpointRequired;
    }
    const selectedPermissions = Object.values(checkedItems).some(Boolean);
    if (!selectedPermissions) {
      errors.permissions = commonValidationMsg.selectPermission;
    }
    setErrorData(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const payload = {
        ...permissionData,
        navItemSlugs: getSelectedNavItemNames(
          checkedItems,
          adminMenuPermissions
        )
      };
      const response = isEdit
        ? await privateAxios.put(
            getUserManagementPermissionsDataURL(id),
            payload
          )
        : await privateAxios.post(getUserManagementPermissionsURL, payload);
      showToast(response?.data?.message, showToastType.success);
      navigate(USER_PERMISSION);
    } catch (error) {
      showToast(error?.response?.data?.message, showToastType.error);
    }
  };
  const onCancel = () => {
    navigate(USER_PERMISSION);
  };
  return (
    <>
      <TablePagesTitle
        mainTitle={userManage?.mainTitle}
        content={permissionManagement?.content}
      />
      <AccessPageAction
        subheading={permissionManagement?.createPermission}
        alluser={USER_PERMISSION}
        allUserTitle={permissionManagement?.subheading}
        createuser={CREATE_USER_PERMISSION}
        createUserTitle={permissionManagement?.createPermission}
      />

      {loading ? (
        <CreateRoleSkeleton />
      ) : (
        <div className="row mb-3 mx-0">
          <div className="col-md-12 sale-leade-form mt-3">
            <form onSubmit={handleSubmit}>
              <div className="row mb-2">
                <div className="col-sm-4 col-xl-2">
                  <div className="input-title">
                    <label>{permissionManagement.label1}</label>
                  </div>
                </div>
                <div className="col-sm-8 col-xl-10">
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder={permissionManagement.label1}
                      name="name"
                      maxLength={30}
                      value={permissionData.name}
                      onChange={handleInputChange}
                    />
                  </div>
                  {errorData.name && (
                    <div className="text-danger small">{errorData.name}</div>
                  )}
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4 col-xl-2">
                  <div className="input-title">
                    <label>{permissionManagement.label2}</label>
                  </div>
                </div>
                <div className="col-sm-8 col-xl-10">
                  <div className="input-group">
                    <select
                      name="isActive"
                      id="isActive"
                      value={permissionData.isActive}
                      onChange={handleInputChange}
                    >
                      {getActiveStatusOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4 col-xl-2">
                  <div className="input-title">
                    <label>{permissionManagement.label3}</label>
                  </div>
                </div>
                <div className="col-sm-8 col-xl-10">
                  <div className="input-group">
                    <input
                      type="text"
                      name="endpoint"
                      placeholder={permissionManagement.label3}
                      value={permissionData.endpoint}
                      onChange={handleInputChange}
                    />
                  </div>
                  {errorData.endpoint && (
                    <div className="text-danger small">
                      {errorData.endpoint}
                    </div>
                  )}
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4 col-xl-2">
                  <div className="input-title">
                    <label>{permissionManagement.label5}</label>
                  </div>
                </div>
                <div className="col-sm-8 col-xl-10">
                  <div className="input-group">
                    <input
                      type="text"
                      name="method"
                      placeholder={permissionManagement.label5}
                      value={permissionData.method}
                      onChange={handleInputChange}
                    />
                  </div>
                  {errorData.method && (
                    <div className="text-danger small">{errorData.method}</div>
                  )}
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4 col-xl-2">
                  <div className="input-title">
                    <label>{permissionManagement.label4}</label>
                  </div>
                </div>
                <div className="col-sm-8 col-xl-10">
                  <div className="input-group">
                    <textarea
                      name="description"
                      value={permissionData.description}
                      placeholder={permissionManagement.label4}
                      rows={4}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4 col-xl-2">
                  <div className="input-title">
                    <label>{roleMange.label6}</label>
                  </div>
                </div>
                <div className="col-sm-8 col-xl-10">
                  <div className="border p-3 height-with-scroll">
                    {adminMenuPermissions?.map((item) => (
                      <div key={item.id} className="mb-3 permission-group">
                        <div className="mb-2">
                          <div className="input-title">
                            <label className="cursor-pointer">
                              <input
                                type="checkbox"
                                className="me-1 select-all"
                                checked={checkedItems[item.id] || false}
                                onChange={(e) =>
                                  handleSelectAll(item.id, e.target.checked)
                                }
                              />
                              {item.name}
                            </label>
                          </div>
                        </div>
                        {item.children && item.children.length > 0 && (
                          <div className="row permissions-list">
                            {item.children.map((child) => (
                              <div className="col-lg-6 col-xl-4" key={child.id}>
                                <input
                                  type="checkbox"
                                  id={`menu-${child.id}`}
                                  checked={checkedItems[child.id] || false}
                                  onChange={(e) =>
                                    handleChildChange(
                                      item.id,
                                      child.id,
                                      e.target.checked
                                    )
                                  }
                                />
                                <label
                                  htmlFor={`menu-${child.id}`}
                                  className="ms-1 cursor-pointer"
                                >
                                  {child.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {errorData.permissions && (
                    <div className="text-danger small mt-1">
                      {errorData.permissions}
                    </div>
                  )}
                </div>
              </div>

              <div className="row mt-4">
                <div className="col-sm-8 offset-sm-2 d-flex d-sm-block justify-content-center justify-content-sm-start">
                  <button
                    type="button"
                    className="btn btn-red me-2"
                    onClick={onCancel}
                  >
                    {buttonName.cancel}
                  </button>

                  <button type="submit" className="btn btn-primary">
                    {isEdit ? buttonName.update : buttonName.create}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
export default PermissionForm;
